/**
 * Club product page behaviour: variant switching, add to bag (via the theme's cart events),
 * tabs and the "Style with" carousel.
 *
 * Everything is delegated from `document` and keyed off `[data-cc-pdp]`, so it keeps working when
 * the theme editor re-renders the section.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const MOBILE_QUERY = window.matchMedia('(max-width: 989px)');
const dataCache = new WeakMap();

/** @param {Element | null} el */
function rootOf(el) {
  return el instanceof Element ? el.closest('[data-cc-pdp]') : null;
}

/** @param {Element} root */
function getData(root) {
  let data = dataCache.get(root);
  if (!data) {
    data = JSON.parse(root.querySelector('[data-cc-pdp-json]')?.textContent || '{"variants":[]}');
    dataCache.set(root, data);
  }
  return data;
}

/** Currently selected option values, indexed by option position. @param {Element} root */
function selectedOptions(root) {
  const selected = [];
  for (const fieldset of root.querySelectorAll('[data-option-index]')) {
    const index = Number(fieldset.getAttribute('data-option-index'));
    selected[index] = fieldset.querySelector('input:checked')?.value ?? null;
  }
  return selected;
}

/** @param {{variants: any[]}} data @param {(string|null)[]} options */
function findVariant(data, options) {
  return data.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
}

/** Marks option values whose variant (with the other current selections) is sold out / missing. */
function updateAvailability(root, data, options) {
  const threshold = Number(root.dataset.threshold) || 0;

  for (const fieldset of root.querySelectorAll('[data-option-index]')) {
    const index = Number(fieldset.getAttribute('data-option-index'));

    for (const label of fieldset.querySelectorAll('label')) {
      const input = label.querySelector('input');
      if (!input) continue;

      const candidate = options.slice();
      candidate[index] = input.value;
      const variant = findVariant(data, candidate);
      label.classList.toggle('is-soldout', !variant || !variant.available);

      const stock = variant?.stock;
      label.classList.toggle('is-low', typeof stock === 'number' && stock > 0 && stock <= threshold);
    }
  }
}

function updateStock(root, variant) {
  const threshold = Number(root.dataset.threshold) || 0;
  const stock = variant?.stock;
  const isLow = typeof stock === 'number' && stock > 0 && stock <= threshold;

  root.classList.toggle('is-low', isLow);

  const stockEl = root.querySelector('[data-cc-stock]');
  const textEl = root.querySelector('[data-cc-stock-text]');
  if (stockEl && textEl) {
    stockEl.hidden = !isLow;
    if (isLow) textEl.textContent = `${stock} ${stock === 1 ? 'item' : 'items'} left`;
  }

  const meta = root.querySelector('.cc-pdp__meta');
  if (meta) meta.hidden = !isLow && !meta.querySelector('.cc-pdp__size-guide');
}

function updatePrice(root, variant) {
  const priceEl = root.querySelector('[data-cc-price]');
  if (!priceEl || !variant) return;

  priceEl.innerHTML =
    variant.compare_at_price > variant.price
      ? `<span class="is-sale">${variant.price_formatted}</span> <s>${variant.compare_formatted}</s>`
      : `<span>${variant.price_formatted}</span>`;
}

function updateButton(root, variant) {
  const button = root.querySelector('[data-cc-add]');
  const label = root.querySelector('[data-cc-add-label]');
  if (!button || !label) return;

  const available = Boolean(variant?.available);
  button.disabled = !available;
  label.textContent = variant
    ? available
      ? root.dataset.labelAdd
      : root.dataset.labelSoldout
    : root.dataset.labelUnavailable;
}

function scrollToMedia(root, variant) {
  const mediaId = variant?.featured_media_id;
  if (!mediaId) return;

  const media = root.querySelector(`[data-media-id="${mediaId}"]`);
  const gallery = root.querySelector('[data-cc-gallery]');
  if (!media || !gallery) return;

  if (MOBILE_QUERY.matches) {
    gallery.scrollTo({ left: media.offsetLeft, behavior: 'smooth' });
  } else {
    media.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/** @param {Element} root @param {{scroll?: boolean}} [opts] */
function refresh(root, { scroll = false } = {}) {
  const data = getData(root);
  const options = selectedOptions(root);
  const variant = findVariant(data, options);

  for (const el of root.querySelectorAll('[data-cc-option-current]')) {
    const checked = el.closest('[data-option-index]')?.querySelector('input:checked');
    if (checked) el.textContent = checked.value;
  }

  updateAvailability(root, data, options);
  updatePrice(root, variant);
  updateButton(root, variant);
  updateStock(root, variant);

  const idInput = root.querySelector('[data-cc-variant-id]');
  if (idInput && variant) idInput.value = String(variant.id);

  if (variant && scroll) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url);
    scrollToMedia(root, variant);
  }

  const msg = root.querySelector('[data-cc-msg]');
  if (msg) msg.textContent = '';
}

async function fetchCart(root) {
  const response = await fetch(root.dataset.cartUrl, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/** @param {HTMLFormElement} form */
async function addToCart(form) {
  const root = rootOf(form);
  if (!root) return;

  const button = root.querySelector('[data-cc-add]');
  const label = root.querySelector('[data-cc-add-label]');
  const msg = root.querySelector('[data-cc-msg]');
  if (!button || !label || button.disabled || button.getAttribute('aria-busy') === 'true') return;

  const original = label.textContent;
  button.setAttribute('aria-busy', 'true');
  if (msg) msg.textContent = '';

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity')) || 1;

  // Ask the theme's cart to re-render itself server side and hand the HTML back with the add.
  const sectionIds = [];
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
  }
  if (sectionIds.length) formData.set('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  const detailBase = {
    source: 'cc-product',
    sourceId: root.id,
    itemCount: quantity,
    productId: root.dataset.productId,
  };

  try {
    const response = await fetch(root.dataset.cartAddUrl, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: formData,
      credentials: 'same-origin',
    });
    const result = await response.json();

    if (result.status) {
      const message = result.description || result.message || 'Could not add to bag';
      if (msg) msg.textContent = message;

      form.dispatchEvent(
        new CartErrorEvent({
          error: result.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: result.description, errors: result.errors },
        })
      );

      const cart = await fetchCart(root).catch(() => null);
      if (cart) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detailBase, items: cart.items, didError: true },
        });
      } else {
        deferred.reject(new Error(message));
      }
      label.textContent = original;
      return;
    }

    const cart = await fetchCart(root);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { ...detailBase, items: cart.items, sections: result.sections, didError: false },
    });

    label.textContent = root.dataset.labelAdded || 'Added';
    setTimeout(() => {
      if (label.textContent === (root.dataset.labelAdded || 'Added')) label.textContent = original;
    }, 1800);
  } catch (error) {
    console.error('[cc-product] add to bag failed:', error);
    deferred.reject(error);
    if (msg) msg.textContent = 'Something went wrong. Please try again.';
    label.textContent = original;
    form.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    button.removeAttribute('aria-busy');
  }
}

function selectTab(tab) {
  const tabs = tab.closest('[data-cc-tabs]');
  if (!tabs) return;

  for (const other of tabs.querySelectorAll('[data-cc-tab]')) {
    other.setAttribute('aria-selected', String(other === tab));
  }
  for (const panel of tabs.querySelectorAll('[data-cc-tabpanel]')) {
    panel.hidden = panel.getAttribute('data-cc-tabpanel') !== tab.getAttribute('data-cc-tab');
  }
}

/** Loads "Style with" products: complementary recommendations first, related as a fallback. */
async function loadStyleWith(root) {
  const box = root.querySelector('[data-cc-stylewith]');
  if (!box || box.dataset.loaded) return;
  box.dataset.loaded = 'true';

  const track = box.querySelector('[data-cc-stylewith-track]');
  if (!track) return;

  for (const intent of ['complementary', 'related']) {
    try {
      const response = await fetch(`${box.dataset.url}&intent=${intent}`, { credentials: 'same-origin' });
      if (!response.ok) continue;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = doc.querySelector('[data-cc-stylewith-items]');
      if (items && items.children.length > 0) {
        track.innerHTML = items.innerHTML;
        box.hidden = false;
        return;
      }
    } catch (_) {
      // try the next intent
    }
  }
}

function scrollStyleWith(button, direction) {
  const box = button.closest('[data-cc-stylewith]');
  const track = box?.querySelector('[data-cc-stylewith-track]');
  if (!track) return;

  const tile = track.querySelector('.cc-pdp__tile');
  const step = tile ? tile.getBoundingClientRect().width + 2 : track.clientWidth * 0.8;
  track.scrollBy({ left: direction * step, behavior: 'smooth' });
}

function init(root) {
  if (!(root instanceof Element) || root.dataset.ccInit) return;
  root.dataset.ccInit = 'true';

  refresh(root);
  loadStyleWith(root);
}

function initAll() {
  for (const root of document.querySelectorAll('[data-cc-pdp]')) init(root);
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

  const root = rootOf(target);
  if (root && target.closest('[data-option-index]')) refresh(root, { scroll: true });
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-cc-form]') || !rootOf(form)) return;

  event.preventDefault();
  addToCart(form);
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const tab = target.closest('[data-cc-tab]');
  if (tab) {
    selectTab(tab);
    return;
  }

  const prev = target.closest('[data-cc-sw-prev]');
  if (prev) return scrollStyleWith(prev, -1);

  const next = target.closest('[data-cc-sw-next]');
  if (next) scrollStyleWith(next, 1);
});

document.addEventListener('shopify:section:load', initAll);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
